import { useState, useEffect } from 'react';
import { Plus, Building2, Users, Layers, ChevronRight, ChevronDown, Edit2, Trash2, DollarSign } from 'lucide-react';
import axios from 'axios';

interface Entity {
  id: string;
  code: string;
  name: string;
  legalName?: string;
  entityType: 'holding' | 'subsidiary' | 'branch' | 'division';
  currency: string;
  consolidationMethod: string;
  ownershipPercentage: number;
  isConsolidated: boolean;
  level: number;
  children?: Entity[];
}

interface Division {
  id: string;
  code: string;
  name: string;
  divisionType: string;
  isProfitCenter: boolean;
  isCostCenter: boolean;
  children?: Department[];
}

interface Department {
  id: string;
  code: string;
  name: string;
  departmentType: string;
  headcount: number;
  costCenterCode?: string;
}

const OrganizationalHierarchy = () => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Entity form state
  const [entityForm, setEntityForm] = useState({
    code: '',
    name: '',
    legalName: '',
    entityType: 'subsidiary',
    parentEntityId: '',
    currency: 'USD',
    consolidationMethod: 'full',
    ownershipPercentage: 100,
    isConsolidated: true,
  });

  // Division form state
  const [divisionForm, setDivisionForm] = useState({
    code: '',
    name: '',
    divisionType: 'business_unit',
    isProfitCenter: true,
    isCostCenter: false,
    isInvestmentCenter: false,
  });

  // Department form state
  const [departmentForm, setDepartmentForm] = useState({
    code: '',
    name: '',
    departmentType: 'operations',
    divisionId: '',
    costCenterCode: '',
    monthlyBudget: '',
    annualBudget: '',
    headcount: 0,
  });

  useEffect(() => {
    fetchEntityHierarchy();
  }, []);

  const fetchEntityHierarchy = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/hierarchy/entities');
      setEntities(response.data.entities || []);
    } catch (error) {
      console.error('Error fetching entity hierarchy:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async (entityId: string) => {
    try {
      const response = await axios.get(`/api/v1/hierarchy/entities/${entityId}/divisions`);
      setDivisions(response.data.divisions || []);
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  };

  const handleCreateEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post('/api/v1/hierarchy/entities', entityForm);
      setShowEntityModal(false);
      fetchEntityHierarchy();
      resetEntityForm();
    } catch (error) {
      console.error('Error creating entity:', error);
      alert('Failed to create entity');
    }
  };

  const handleCreateDivision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntity) return;

    try {
      await axios.post('/api/v1/hierarchy/divisions', {
        ...divisionForm,
        entityId: selectedEntity.id,
      });
      setShowDivisionModal(false);
      fetchDivisions(selectedEntity.id);
      resetDivisionForm();
    } catch (error) {
      console.error('Error creating division:', error);
      alert('Failed to create division');
    }
  };

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntity) return;

    try {
      await axios.post('/api/v1/hierarchy/departments', {
        ...departmentForm,
        entityId: selectedEntity.id,
        monthlyBudget: departmentForm.monthlyBudget ? parseFloat(departmentForm.monthlyBudget) : undefined,
        annualBudget: departmentForm.annualBudget ? parseFloat(departmentForm.annualBudget) : undefined,
      });
      setShowDepartmentModal(false);
      if (departmentForm.divisionId) {
        fetchDivisions(selectedEntity.id);
      }
      resetDepartmentForm();
    } catch (error) {
      console.error('Error creating department:', error);
      alert('Failed to create department');
    }
  };

  const resetEntityForm = () => {
    setEntityForm({
      code: '',
      name: '',
      legalName: '',
      entityType: 'subsidiary',
      parentEntityId: '',
      currency: 'USD',
      consolidationMethod: 'full',
      ownershipPercentage: 100,
      isConsolidated: true,
    });
  };

  const resetDivisionForm = () => {
    setDivisionForm({
      code: '',
      name: '',
      divisionType: 'business_unit',
      isProfitCenter: true,
      isCostCenter: false,
      isInvestmentCenter: false,
    });
  };

  const resetDepartmentForm = () => {
    setDepartmentForm({
      code: '',
      name: '',
      departmentType: 'operations',
      divisionId: '',
      costCenterCode: '',
      monthlyBudget: '',
      annualBudget: '',
      headcount: 0,
    });
  };

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleEntityClick = (entity: Entity) => {
    setSelectedEntity(entity);
    fetchDivisions(entity.id);
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'holding':
        return <Building2 className="w-5 h-5 text-blue-600" />;
      case 'subsidiary':
        return <Building2 className="w-5 h-5 text-green-600" />;
      case 'branch':
        return <Building2 className="w-5 h-5 text-purple-600" />;
      default:
        return <Building2 className="w-5 h-5 text-neutral-600" />;
    }
  };

  const renderEntityTree = (entities: Entity[], parentId?: string) => {
    return entities.map((entity) => {
      const hasChildren = entity.children && entity.children.length > 0;
      const isExpanded = expandedNodes.has(entity.id);
      const isSelected = selectedEntity?.id === entity.id;

      return (
        <div key={entity.id} className="ml-4">
          <div
            className={`flex items-center p-3 rounded-lg cursor-pointer transition ${
              isSelected
                ? 'bg-primary-100 border-2 border-primary-500'
                : 'hover:bg-neutral-50 border border-neutral-200'
            }`}
            onClick={() => handleEntityClick(entity)}
          >
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(entity.id);
                }}
                className="mr-2"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-neutral-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-neutral-600" />
                )}
              </button>
            )}

            {!hasChildren && <div className="w-6 mr-2" />}

            {getEntityIcon(entity.entityType)}

            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {entity.name}
                    <span className="ml-2 text-xs text-neutral-500">({entity.code})</span>
                  </p>
                  <p className="text-xs text-neutral-600 mt-1">
                    {entity.entityType} • {entity.currency} • {entity.ownershipPercentage}% ownership
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  {entity.isConsolidated && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      Consolidated
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs rounded ${
                    entity.entityType === 'holding'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-neutral-100 text-neutral-700'
                  }`}>
                    Level {entity.level}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div className="mt-2">{renderEntityTree(entity.children!)}</div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading organizational hierarchy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Organizational Hierarchy</h1>
        <p className="text-neutral-600">
          Manage multi-entity, multi-division, multi-department structure for enterprise consolidation
        </p>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex items-center space-x-4">
        <button
          onClick={() => setShowEntityModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Entity</span>
        </button>

        {selectedEntity && (
          <>
            <button
              onClick={() => setShowDivisionModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
            >
              <Layers className="w-4 h-4" />
              <span>Add Division</span>
            </button>

            <button
              onClick={() => setShowDepartmentModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center space-x-2"
            >
              <Users className="w-4 h-4" />
              <span>Add Department</span>
            </button>
          </>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left: Entity Tree */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Legal Entities
          </h2>

          {entities.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-600 mb-4">No entities created yet</p>
              <button
                onClick={() => setShowEntityModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Create First Entity
              </button>
            </div>
          ) : (
            <div className="space-y-2">{renderEntityTree(entities)}</div>
          )}
        </div>

        {/* Right: Divisions & Departments */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          {selectedEntity ? (
            <>
              <div className="mb-6 pb-4 border-b border-neutral-200">
                <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                  {selectedEntity.name}
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-600">Code:</span>
                    <span className="ml-2 font-medium">{selectedEntity.code}</span>
                  </div>
                  <div>
                    <span className="text-neutral-600">Type:</span>
                    <span className="ml-2 font-medium capitalize">{selectedEntity.entityType}</span>
                  </div>
                  <div>
                    <span className="text-neutral-600">Currency:</span>
                    <span className="ml-2 font-medium">{selectedEntity.currency}</span>
                  </div>
                  <div>
                    <span className="text-neutral-600">Ownership:</span>
                    <span className="ml-2 font-medium">{selectedEntity.ownershipPercentage}%</span>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                <Layers className="w-5 h-5 mr-2" />
                Divisions & Departments
              </h3>

              {divisions.length === 0 ? (
                <div className="text-center py-12">
                  <Layers className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-600 mb-4">No divisions created yet</p>
                  <button
                    onClick={() => setShowDivisionModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Create First Division
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {divisions.map((division) => (
                    <div key={division.id} className="border border-neutral-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-neutral-900">
                            {division.name}
                            <span className="ml-2 text-xs text-neutral-500">({division.code})</span>
                          </h4>
                          <p className="text-xs text-neutral-600 mt-1">
                            {division.divisionType.replace('_', ' ')}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          {division.isProfitCenter && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                              P&L
                            </span>
                          )}
                          {division.isCostCenter && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                              Cost
                            </span>
                          )}
                        </div>
                      </div>

                      {division.children && division.children.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-neutral-200 space-y-2">
                          {division.children.map((dept) => (
                            <div key={dept.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 text-neutral-400 mr-2" />
                                <span className="text-neutral-900">{dept.name}</span>
                                <span className="ml-2 text-xs text-neutral-500">({dept.code})</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-neutral-600">
                                {dept.headcount > 0 && (
                                  <span>{dept.headcount} people</span>
                                )}
                                {dept.costCenterCode && (
                                  <span className="px-2 py-1 bg-neutral-100 rounded">
                                    {dept.costCenterCode}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-600">Select an entity to view divisions and departments</p>
            </div>
          )}
        </div>
      </div>

      {/* Entity Modal */}
      {showEntityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Create New Entity</h2>

            <form onSubmit={handleCreateEntity} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={entityForm.code}
                    onChange={(e) => setEntityForm({ ...entityForm, code: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., SUB1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Entity Type *
                  </label>
                  <select
                    value={entityForm.entityType}
                    onChange={(e) => setEntityForm({ ...entityForm, entityType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="holding">Holding Company</option>
                    <option value="subsidiary">Subsidiary</option>
                    <option value="branch">Branch</option>
                    <option value="division">Division</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={entityForm.name}
                  onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Acme Software Inc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Legal Name
                </label>
                <input
                  type="text"
                  value={entityForm.legalName}
                  onChange={(e) => setEntityForm({ ...entityForm, legalName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Official legal name"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={entityForm.currency}
                    onChange={(e) => setEntityForm({ ...entityForm, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Ownership %
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={entityForm.ownershipPercentage}
                    onChange={(e) => setEntityForm({ ...entityForm, ownershipPercentage: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Consolidation
                  </label>
                  <select
                    value={entityForm.consolidationMethod}
                    onChange={(e) => setEntityForm({ ...entityForm, consolidationMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="full">Full</option>
                    <option value="proportionate">Proportionate</option>
                    <option value="equity">Equity Method</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isConsolidated"
                  checked={entityForm.isConsolidated}
                  onChange={(e) => setEntityForm({ ...entityForm, isConsolidated: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isConsolidated" className="text-sm text-neutral-700">
                  Include in consolidated financial statements
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEntityModal(false);
                    resetEntityForm();
                  }}
                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Entity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Division Modal */}
      {showDivisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Create New Division</h2>

            <form onSubmit={handleCreateDivision} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={divisionForm.code}
                    onChange={(e) => setDivisionForm({ ...divisionForm, code: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., ENG"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={divisionForm.divisionType}
                    onChange={(e) => setDivisionForm({ ...divisionForm, divisionType: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="business_unit">Business Unit</option>
                    <option value="product_line">Product Line</option>
                    <option value="geographic">Geographic</option>
                    <option value="function">Function</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={divisionForm.name}
                  onChange={(e) => setDivisionForm({ ...divisionForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Engineering Division"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-neutral-700">
                  Responsibility Center
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={divisionForm.isProfitCenter}
                      onChange={(e) => setDivisionForm({ ...divisionForm, isProfitCenter: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Profit Center</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={divisionForm.isCostCenter}
                      onChange={(e) => setDivisionForm({ ...divisionForm, isCostCenter: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Cost Center</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={divisionForm.isInvestmentCenter}
                      onChange={(e) => setDivisionForm({ ...divisionForm, isInvestmentCenter: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Investment Center</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDivisionModal(false);
                    resetDivisionForm();
                  }}
                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Create Division
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Modal */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Create New Department</h2>

            <form onSubmit={handleCreateDepartment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Division
                </label>
                <select
                  value={departmentForm.divisionId}
                  onChange={(e) => setDepartmentForm({ ...departmentForm, divisionId: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a division (optional)</option>
                  {divisions.map((div) => (
                    <option key={div.id} value={div.id}>
                      {div.name} ({div.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={departmentForm.code}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, code: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., DEV"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={departmentForm.departmentType}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, departmentType: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="operations">Operations</option>
                    <option value="sales">Sales</option>
                    <option value="marketing">Marketing</option>
                    <option value="finance">Finance</option>
                    <option value="hr">Human Resources</option>
                    <option value="it">IT</option>
                    <option value="support">Support</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Software Development"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Cost Center Code
                  </label>
                  <input
                    type="text"
                    value={departmentForm.costCenterCode}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, costCenterCode: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., CC-1001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Headcount
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={departmentForm.headcount}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, headcount: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Monthly Budget
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={departmentForm.monthlyBudget}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, monthlyBudget: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Annual Budget
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={departmentForm.annualBudget}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, annualBudget: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentModal(false);
                    resetDepartmentForm();
                  }}
                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create Department
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationalHierarchy;
